// Two-factor (TOTP) management modal.
//
// Three states drive the body:
//   • Not enabled        → "Set up" CTA opens the enrollment flow
//   • Enrolling (pending) → QR + manual secret + 6-digit verify code
//   • Enabled            → status row + regen recovery codes + disable
//
// Recovery codes returned by totp.enable / regenerateRecoveryCodes
// are shown ONCE — the modal pins them at the top with a copy-all
// affordance until the user dismisses.

import { useEffect, useState } from "preact/hooks";
import QRCode from "qrcode";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { api } from "../../lib/rpc";

interface TotpModalProps {
  open: boolean;
  onClose: () => void;
  notify: (msg: string) => void;
}

type Phase = "loading" | "off" | "enrolling" | "on";

export function TotpModal({ open, onClose, notify }: TotpModalProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [recoveryRemaining, setRecoveryRemaining] = useState(0);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [manualSecret, setManualSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Recovery codes to display once after enable or regen. Stored
  // until the user explicitly clicks "I've saved these" — without
  // an explicit acknowledgement it's too easy to dismiss and lose
  // them.
  const [pendingRecovery, setPendingRecovery] = useState<string[] | null>(null);

  // Boot — fetch status on each open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("loading");
    setCode("");
    setPendingRecovery(null);
    api.totpStatus()
      .then((s) => {
        if (cancelled) return;
        setRecoveryRemaining(s.recoveryCodesRemaining);
        setPhase(s.enabled ? "on" : "off");
      })
      .catch(() => { if (!cancelled) setPhase("off"); });
    return () => { cancelled = true; };
  }, [open]);

  // Escape closes — but only when nothing's mid-flight or showing
  // freshly-minted recovery codes that the user MUST save.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pendingRecovery && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, pendingRecovery, busy]);

  // Render the otpauthUrl to a data URL the moment we have one. Done
  // here (not at TS-level constant) because QRCode.toDataURL is async.
  useEffect(() => {
    if (!otpauthUrl) { setQrDataUrl(null); return; }
    QRCode.toDataURL(otpauthUrl, { margin: 1, scale: 5 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [otpauthUrl]);

  if (!open) return null;

  const startEnrollment = async () => {
    setBusy(true);
    try {
      const r = await api.totpSetup();
      setOtpauthUrl(r.otpauthUrl);
      setManualSecret(r.secret);
      setPhase("enrolling");
      setCode("");
    } catch (e: any) {
      notify(e?.message || "Couldn't start enrollment");
    } finally { setBusy(false); }
  };

  const verifyAndEnable = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) { notify("Enter the 6-digit code from your authenticator"); return; }
    setBusy(true);
    try {
      const r = await api.totpEnable(c);
      setPendingRecovery(r.recoveryCodes);
      setRecoveryRemaining(r.recoveryCodes.length);
      setPhase("on");
      setOtpauthUrl(null);
      setManualSecret(null);
      setCode("");
      notify("2FA enabled");
    } catch (e: any) {
      notify(e?.message || "Invalid code");
    } finally { setBusy(false); }
  };

  const regenRecovery = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) { notify("Enter the current 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.totpRegenerateRecoveryCodes(c);
      setPendingRecovery(r.recoveryCodes);
      setRecoveryRemaining(r.recoveryCodes.length);
      setCode("");
      notify("Recovery codes regenerated");
    } catch (e: any) {
      notify(e?.message || "Invalid code");
    } finally { setBusy(false); }
  };

  const disable2fa = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) { notify("Enter the current 6-digit code"); return; }
    const ok = await confirm({
      title: "Disable 2FA?",
      message: "Your account will only require a password to sign in.",
      confirmLabel: "Disable 2FA",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.totpDisable(c);
      setPhase("off");
      setCode("");
      notify("2FA disabled");
    } catch (e: any) {
      notify(e?.message || "Invalid code");
    } finally { setBusy(false); }
  };

  const copyRecovery = async () => {
    if (!pendingRecovery) return;
    try { await navigator.clipboard.writeText(pendingRecovery.join("\n")); notify("Codes copied"); }
    catch { notify("Couldn't copy — select the codes manually"); }
  };

  return (
    <div className="settings-submodal-bg" onClick={(e) => { if (e.target === e.currentTarget && !pendingRecovery && !busy) onClose(); }}>
      <div className="settings-submodal" onClick={(e) => e.stopPropagation()}>
        <header className="settings-submodal-head">
          <h3 className="settings-submodal-title">Two-factor auth</h3>
          <button className="icon-btn" onClick={onClose} disabled={!!pendingRecovery || busy} type="button" title={pendingRecovery ? "Save your recovery codes first" : "Close"}>
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="settings-submodal-body">
          {pendingRecovery && (
            <RecoveryCodesPanel codes={pendingRecovery} onCopy={copyRecovery} onAck={() => setPendingRecovery(null)} />
          )}

          {phase === "loading" && <p className="settings-loading">Loading…</p>}

          {phase === "off" && !pendingRecovery && (
            <div className="settings-stack">
              <p className="settings-paragraph">
                Add a code from an authenticator app (1Password, Authy, Google Authenticator)
                every time you sign in. Mandatory for admins, optional for everyone else.
              </p>
              <button
                className="btn btn-primary"
                onClick={startEnrollment}
                disabled={busy}
                type="button"
              >
                <Icon name="shield" size={13} />
                {busy ? "Preparing…" : "Set up 2FA"}
              </button>
            </div>
          )}

          {phase === "enrolling" && otpauthUrl && (
            <div className="settings-stack">
              <p className="settings-paragraph">
                Scan this QR with your authenticator, then enter the 6-digit code it shows.
              </p>
              <div className="settings-qr-wrap">
                {qrDataUrl
                  ? <img className="settings-qr" src={qrDataUrl} alt="2FA QR code" />
                  : <div className="settings-qr-skel" />}
              </div>
              {manualSecret && (
                <details className="settings-details">
                  <summary>Can't scan? Enter the secret manually</summary>
                  <code className="settings-mono-block">{manualSecret}</code>
                </details>
              )}
              <Code6 value={code} onInput={setCode} onSubmit={verifyAndEnable} disabled={busy} />
              <button
                className="btn btn-primary"
                onClick={verifyAndEnable}
                disabled={busy || !/^\d{6}$/.test(code.trim())}
                type="button"
              >
                <Icon name="check" size={13} />
                {busy ? "Verifying…" : "Verify and enable"}
              </button>
            </div>
          )}

          {phase === "on" && !pendingRecovery && (
            <div className="settings-stack">
              <div className="settings-status-card" data-tone="ok">
                <Icon name="shield" size={14} />
                <div style={{ flex: 1 }}>
                  <div className="settings-status-title">2FA is on</div>
                  <div className="settings-status-sub">
                    {recoveryRemaining} recovery {recoveryRemaining === 1 ? "code" : "codes"} remaining
                  </div>
                </div>
              </div>

              <div className="settings-paragraph">
                Enter your current 6-digit code to regenerate recovery codes or disable 2FA.
              </div>
              <Code6 value={code} onInput={setCode} onSubmit={regenRecovery} disabled={busy} />
              <div className="settings-button-row">
                <button className="btn" onClick={regenRecovery} disabled={busy} type="button">
                  <Icon name="key" size={13} /> Regenerate codes
                </button>
                <button className="btn settings-danger-btn" onClick={disable2fa} disabled={busy} type="button">
                  <Icon name="x" size={13} /> Disable 2FA
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 6-digit code input. Auto-focuses on mount; submits on Enter.
function Code6({ value, onInput, onSubmit, disabled }: { value: string; onInput: (v: string) => void; onSubmit: () => void; disabled?: boolean }) {
  return (
    <input
      className="input settings-code6"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      placeholder="••••••"
      value={value}
      disabled={disabled}
      autoFocus
      onInput={(e) => {
        const v = (e.currentTarget as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6);
        onInput(v);
      }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSubmit(); } }}
    />
  );
}

function RecoveryCodesPanel({ codes, onCopy, onAck }: { codes: string[]; onCopy: () => void; onAck: () => void }) {
  return (
    <div className="settings-recovery">
      <div className="settings-recovery-head">
        <Icon name="key" size={14} />
        <strong>Save these recovery codes</strong>
      </div>
      <p className="settings-paragraph">
        Store these somewhere safe. Each lets you sign in once if you lose access to your
        authenticator. You won't be able to see them again.
      </p>
      <ul className="settings-recovery-list">
        {codes.map((c) => <li key={c}><code>{c}</code></li>)}
      </ul>
      <div className="settings-button-row">
        <button className="btn" onClick={onCopy} type="button">
          <Icon name="check" size={13} /> Copy all
        </button>
        <button className="btn btn-primary" onClick={onAck} type="button">
          I've saved these
        </button>
      </div>
    </div>
  );
}
